// When dealing with relative dates, how often should we refresh. This should be
// tuned to match the relative date library, but should be acceptable without
// doing so.

export const FREQUENT_REFRESH   = 10000; // 10 seconds
export const INFREQUENT_REFRESH = 60000; // 60 seconds

const RECENT_THRESHOLD = 120000; // 2 minutes

export const ONE_MINUTE_MS = 1000 * 60;
export const ONE_HOUR_MS = ONE_MINUTE_MS * 60;

export const recentDate = (date: Date, now: number): boolean =>
  (Math.abs(date.getTime() - now) < RECENT_THRESHOLD);

export const refreshIntervalForDate = (date: Date, now: number): number =>
  (recentDate(date, now) ? FREQUENT_REFRESH : INFREQUENT_REFRESH);

// When working with HTML5 Date / Time inputs, they are normalized to a
// particular format

export const BROWSER_DATE_FORMAT = 'YYYY-MM-DD';
export const BROWSER_TIME_FORMAT = 'HH:mm';
export const BROWSER_FORMAT      = `${BROWSER_DATE_FORMAT}T${BROWSER_TIME_FORMAT}`;

export const BROWSER_DATE_FORMAT_REGEX = '\\d{4}-(0[1-9]|1[0-2])-([012]\\d|3[01])';

type MaybeDate = Date | null | undefined;
export const datesEqual = (x: MaybeDate, y: MaybeDate): boolean =>
  (x && x.getTime()) === (y && y.getTime());

/**
 * Takes a relative time in milliseconds and returns a string in the HH:MM:SS format.
 *
 * ```
 * renderHHMMSS((1000 * 60 * 60) * 5 + (1000 * 60) * 32 + (1000) * 7) === '5:32:07'
 * ```
 */
export function renderHHMMSS(timeMs: number): string {
  const hours = Math.floor(timeMs / ONE_HOUR_MS);
  const hoursRemainderMs = timeMs % ONE_HOUR_MS;
  const minutes = Math.floor(hoursRemainderMs / ONE_MINUTE_MS);
  const minutesRemainerMs = hoursRemainderMs % ONE_MINUTE_MS;
  const seconds = Math.floor(minutesRemainerMs / 1000);

  const hoursPart = hours > 0 ? `${hours}:` : '';

  return `${hoursPart}${padZero(minutes)}:${padZero(seconds)}`;
}

/**
 * ```
 * padZero(3) === '03'
 * padZero(12) === '12'
 * ```
 */
export function padZero(timePart: number): string {
  return `${timePart < 10 ? '0' : ''}${timePart}`;
}
